import { ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, effect, inject, signal, viewChild } from '@angular/core';
import { LucideAngularModule, Lock } from 'lucide-angular';
import { AuthService } from '../../../core/services/auth.service';
import { TranslationService } from '../../../core/services/translation.service';
import { Biometrie } from '../../../core/services/biometrie';
import { VerrouPoste } from '../../../core/services/verrou-poste';
import { PinPadComponent } from '../pin-pad/pin-pad.component';
import { initiale } from '../call-overlay/avatars-appel';

const LONGUEUR_NIP = 6;

/**
 * Ecran du poste verrouille : plein ecran, au-dessus de tout, n'accepte que le NIP — ou la biometrie, si la
 * personne l'a activee (touche d'empreinte a gauche du 0).
 *
 * Aucune maquette ne dessine cet ecran ; il reprend la vue « NIP » de la connexion (fond, avatar,
 * points, pave) pour que le geste soit celui que la caissiere connait deja.
 *
 * Trois mauvais NIP effacent le coffre — comme a la connexion — et deconnectent : il n'y a plus de
 * NIP qui vaille, retour au mot de passe.
 */
@Component({
  selector: 'app-ecran-verrou',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LucideAngularModule, PinPadComponent],
  template: `
    <div #racine tabindex="-1" role="dialog" aria-modal="true" [attr.aria-label]="t().verrou.title"
         class="fixed inset-0 z-[100] flex flex-col items-center justify-center p-4 overflow-hidden outline-none bg-gradient-to-br from-primary-950 via-primary-900 to-primary-700">
      <div class="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div class="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary-500/10 blur-3xl"></div>
        <div class="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-accent-500/10 blur-3xl"></div>
      </div>

      <div class="relative z-10 w-full max-w-xs">
        <div class="text-center mb-6">
          <div class="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-2xl shadow-xl shadow-primary-500/30 mb-3">
            {{ initiales() }}
          </div>
          <h2 class="text-white font-semibold text-lg">{{ nom() }}</h2>
          <p class="flex items-center justify-center gap-1.5 text-white/60 text-sm mt-1">
            <lucide-icon [img]="Lock" [size]="14" aria-hidden="true"></lucide-icon>
            {{ t().verrou.title }}
          </p>
          <p class="text-white/40 text-xs mt-1">{{ biometrieActive() ? t().verrou.subtitle_bio : t().verrou.subtitle }}</p>
        </div>

        <app-pin-pad [longueur]="longueur" [saisis]="chiffres().length" [charge]="verification()" [erreur]="erreur()"
                     [libelleSupprimer]="t().verrou.delete"
                     [biometrie]="biometrieActive()" [libelleBiometrie]="t().verrou.biometrie"
                     (chiffre)="ajouter($event)" (supprimer)="retirer()" (biometrieDemandee)="biometrique()" />

        <button type="button" (click)="deconnecter()" class="w-full text-center text-white/40 text-xs py-2 hover:text-white/60 transition-colors">
          {{ t().verrou.logout }}
        </button>
      </div>
    </div>
  `,
})
export class EcranVerrouComponent {
  private readonly verrou = inject(VerrouPoste);
  private readonly auth = inject(AuthService);
  private readonly biometrie = inject(Biometrie);
  readonly t = inject(TranslationService).t;

  readonly Lock = Lock;
  readonly longueur = LONGUEUR_NIP;

  readonly chiffres = signal<string[]>([]);
  readonly erreur = signal<string | null>(null);
  readonly verification = signal(false);
  /** La touche d'empreinte : biometrie enregistree pour cette personne ET possible sur ce poste. */
  readonly biometrieActive = signal(false);

  private readonly racine = viewChild.required<ElementRef<HTMLElement>>('racine');

  readonly nom = computed(() => this.auth.currentUser()?.name ?? '');
  readonly initiales = computed(() =>
    this.nom().split(' ').filter(Boolean).slice(0, 2).map((m) => initiale(m)).join('') || '?',
  );

  constructor() {
    // Le focus entre dans l'ecran : le clavier physique saisit le NIP, et le lecteur d'ecran l'annonce.
    effect(() => this.racine().nativeElement.focus());
    const email = this.auth.currentUser()?.email;
    if (email) void this.biometrie.activePour(email).then((v) => this.biometrieActive.set(v), () => undefined);
  }

  /** Clavier physique : chiffres et retour arriere. */
  @HostListener('window:keydown', ['$event'])
  surTouche(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      this.ajouter(e.key);
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      this.retirer();
    }
  }

  ajouter(chiffre: string): void {
    if (this.verification() || this.chiffres().length >= LONGUEUR_NIP) return;
    this.erreur.set(null);
    const suite = [...this.chiffres(), chiffre];
    this.chiffres.set(suite);
    if (suite.length === LONGUEUR_NIP) void this.verifier(suite.join(''));
  }

  retirer(): void {
    if (this.verification()) return;
    this.chiffres.update((c) => c.slice(0, -1));
    this.erreur.set(null);
  }

  private async verifier(nip: string): Promise<void> {
    this.verification.set(true);
    try {
      const r = await this.verrou.deverrouiller(nip);
      if (r.statut === 'ok') return;
      this.chiffres.set([]);
      if (r.statut === 'coffre-perdu') {
        this.erreur.set(this.t().login.vault_locked);
        setTimeout(() => void this.auth.logout('coffre PIN perdu (ecran de verrou)'), 2000);
        return;
      }
      this.erreur.set(
        r.restants === 1 ? this.t().login.last_attempt : `${this.t().login.wrong_pin} (${r.restants})`,
      );
    } catch {
      this.chiffres.set([]);
      this.erreur.set(this.t().login.generic_error);
    } finally {
      this.verification.set(false);
    }
  }

  /**
   * La touche d'empreinte. Une invite refusee ne dit rien (la personne n'a rien fait de mal) ; une lecture
   * qui echoue renvoie au NIP. Aucun essai du NIP n'est consomme.
   */
  async biometrique(): Promise<void> {
    if (this.verification()) return;
    this.verification.set(true);
    this.erreur.set(null);
    try {
      const r = await this.verrou.deverrouillerParBiometrie();
      if (r === 'echec' || r === 'indisponible') this.erreur.set(this.t().verrou.biometrie_echec);
    } catch {
      this.erreur.set(this.t().verrou.biometrie_echec);
    } finally {
      this.verification.set(false);
    }
  }

  deconnecter(): void {
    void this.auth.logout('bouton Se deconnecter (ecran de verrou)');
  }
}
